import { Undo2 } from "lucide-react";
import type { FC } from "react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { timeActions } from "../actions";
import { initials } from "../review/exportModel";
import type { TimeEntry } from "../types";
import { formatHours } from "../utils";
import { FlatFeeTag } from "./BoardCard";

/** `case_label` is "<client> · <visa>". */
const splitCaseLabel = (entry: TimeEntry) => {
  const parts = entry.case_label.split(" · ");
  const visa = parts.length > 1 ? parts[parts.length - 1] : "";
  const name =
    entry.client_label ||
    (parts.length > 1 ? parts.slice(0, -1).join(" · ") : entry.case_label);
  return { name, visa };
};

/** A released entry row inside the Released list card; recallable until an admin approves it. */
export const ReleasedCard: FC<{ entry: TimeEntry }> = ({ entry }) => {
  const { name, visa } = splitCaseLabel(entry);
  return (
    <div
      data-testid="released-card"
      className="flex items-start gap-3 px-3 py-2.5"
    >
      <Avatar size="sm" className="mt-0.5">
        <AvatarFallback>{initials(name)}</AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-base font-medium text-fg">{name}</span>
          {visa ? <Tag>{visa}</Tag> : null}
          <FlatFeeTag entry={entry} />
        </span>
        <span className="truncate text-sm text-fg-3">{entry.narrative}</span>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-base font-medium text-fg tabular-nums">
          {formatHours(entry.duration_hours)}
        </span>
        {entry.status === "released" ? (
          <Button
            size="xs"
            variant="secondary"
            onClick={() => void timeActions.recall([entry.id])}
          >
            <Undo2 /> Recall
          </Button>
        ) : (
          <StatusBadge
            tone={entry.status === "approved" ? "success" : "neutral"}
          >
            {entry.status_label}
          </StatusBadge>
        )}
      </div>
    </div>
  );
};
